import React, { useState, useEffect } from 'react';
import { X, Banknote, Search, User, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { User as AppUser, Project } from '../../types';

interface ProjectFunderAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
  onSuccess: () => void;
}

export const ProjectFunderAssignmentModal: React.FC<ProjectFunderAssignmentModalProps> = ({
  isOpen,
  onClose,
  project,
  onSuccess
}) => {
  const [funders, setFunders] = useState<AppUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFunder, setSelectedFunder] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadFunders();
      setSelectedFunder(project.assigned_funder_id || '');
      setSearchTerm('');
      setError('');
    }
  }, [isOpen, project.assigned_funder_id]);

  const loadFunders = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('profile_type', 'FINANCEUR')
        .order('full_name');

      if (error) throw error;
      setFunders(data || []);
    } catch (err: any) {
      setError('Erreur lors du chargement des financeurs: ' + err.message);
      console.error('Error loading funders:', err);
      setFunders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedFunder) {
      setError('Veuillez sélectionner un financeur');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { error } = await supabase
        .from('projects')
        .update({
          assigned_funder_id: selectedFunder,
          updated_at: new Date().toISOString()
        })
        .eq('id', project.id);

      if (error) throw error;

      const selectedFunderData = funders.find(f => f.id === selectedFunder);
      if (selectedFunderData) {
        await supabase
          .from('notifications_enhanced')
          .insert({
            user_id: selectedFunder,
            type: 'PROJECT_ASSIGNED',
            title: 'Nouveau projet assigné',
            message: `Le projet "${project.name}" vous a été assigné pour financement`,
            link: `/projects/${project.id}`,
            metadata: {
              project_id: project.id,
              project_name: project.name
            }
          });
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'assignation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnassign = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const { error } = await supabase
        .from('projects')
        .update({
          assigned_funder_id: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', project.id);

      if (error) throw error;

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la désassignation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredFunders = funders.filter(funder => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      funder.full_name?.toLowerCase().includes(searchLower) ||
      funder.email?.toLowerCase().includes(searchLower) ||
      funder.organization?.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Banknote className="w-6 h-6 text-green-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Assigner un financeur</h2>
              <p className="text-sm text-gray-600">{project.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {project.assigned_funder && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-medium text-green-900 mb-2">Financeur actuel</h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {project.assigned_funder.full_name?.charAt(0) || '?'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-green-900">
                      {project.assigned_funder.full_name || project.assigned_funder.email}
                    </p>
                    <p className="text-sm text-green-700">{project.assigned_funder.organization}</p>
                  </div>
                </div>
                <button
                  onClick={handleUnassign}
                  disabled={isSubmitting}
                  className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-md transition-colors text-sm"
                >
                  Désassigner
                </button>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="funder-search" className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher un financeur
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="funder-search"
                type="text"
                placeholder="Nom, email ou organisation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Financeurs disponibles ({filteredFunders.length})
            </h3>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredFunders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <User className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>
                  {searchTerm
                    ? 'Aucun financeur trouvé avec ces critères'
                    : 'Aucun financeur disponible'
                  }
                </p>
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredFunders.map((funder) => (
                  <label
                    key={funder.id}
                    className="flex items-center p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <input
                      type="radio"
                      name="funder"
                      value={funder.id}
                      checked={selectedFunder === funder.id}
                      onChange={(e) => setSelectedFunder(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-medium text-sm">
                          {funder.full_name?.charAt(0) || funder.email?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900">
                            {funder.full_name || funder.email}
                          </p>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Financeur
                          </span>
                        </div>
                        {funder.organization && (
                          <p className="text-sm text-gray-600">{funder.organization}</p>
                        )}
                        <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                          {funder.city && (
                            <div className="flex items-center space-x-1">
                              <MapPin className="w-3 h-3" />
                              <span>{funder.city}</span>
                            </div>
                          )}
                          {funder.phone && (
                            <div className="flex items-center space-x-1">
                              <span>{funder.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedFunder || isSubmitting}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Assignation...</span>
                </div>
              ) : (
                'Assigner'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
